/*
 * Copyright (c) 2020. American Express Company
 */

import $ from 'jquery';
import * as _ from 'lodash';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import { CardModel } from 'src/types/card.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { FullUserModel } from 'src/types/user.model';
import { JaegerContext } from 'src/bw/project-jaeger/JaegerContainer';
import CashFlowService from './cashflow.service';
import FormatService from 'src/services/format.service';

interface BarStats {
    barWidth: number;
    initialMargin: number;
    intraBarMargin: number;
}

interface CardPlacement {
    card: CardModel;
    linkedCard?: CardModel;
    placeStartDate: LocalDate;
    placeEndDate: LocalDate;
}

export default class UnitService {
    public static readonly Selectors = {
        GOALS_TRAY_CONTAINER: '.goals-tray-container',
        WORLDLINE_CONTAINER: '.worldline-container',
        MAIN_STAGE: '.bw-main-stage',
        MAINSTAGE_SCROLL: '#bw-main-scroll',
        DRAWER: '.bw-drawer',
        PAPER: '.MuiDrawer-paper',
        GENERIC_DRAWER: '.genericDrawer',
        VERTICAL_SCALE: '.vertical-scale'
    };

    static SCROLL_LEFT_MARGIN: number = 53; // keeps the bars out from under the vertical scale
    static JAEGER_MARGIN_LEFT: number = 50;

    public static getTimelineEnd(user: FullUserModel): LocalDate {
        return user.cashFlow[user.cashFlow.length - 1].startDate;
    }

    public static getTimelineStart(user: FullUserModel): LocalDate {
        return user?.cashFlow ? user.cashFlow[0].startDate : LocalDate.now();
    }

    public static getDrawerWidth(): number {
        return (
            $(this.Selectors.DRAWER).find(this.Selectors.PAPER).width() ||
            $(this.Selectors.DRAWER).find(this.Selectors.GENERIC_DRAWER).width()
        );
    }

    public static getMaxBarsVisible(barWidth: number): number {
        return Math.ceil(this.getScrollContainerWidth() / Math.abs(barWidth));
    }

    public static getScrollContainerWidth(): number {
        return $(this.Selectors.MAINSTAGE_SCROLL).width();
    }

    public static getScrollWidth(): number {
        return $(this.Selectors.MAINSTAGE_SCROLL).scrollLeft() - UnitService.SCROLL_LEFT_MARGIN;
    }

    public static getZoomLevel(): string {
        const zl: any = $('#zoom').val();
        if (!zl) return 'overall';
        return zl.toString().toLowerCase();
    }

    /**
     * Get the number of days in cashflows per pixel
     * @param user
     * @param totalPixels
     * @param unit ChronoUnit
     */
    public static getTimePerPixel(
        user: FullUserModel,
        totalPixels: number = $(this.Selectors.GOALS_TRAY_CONTAINER).width(),
        unit: ChronoUnit = ChronoUnit.DAYS
    ): number {
        if (isNaN(totalPixels)) totalPixels = $(window).width() * 0.66;
        const firstTimeStamp: LocalDate = this.getTimelineStart(user);
        const lastTimeStamp: LocalDate = this.getTimelineEnd(user);
        const difference: number = Math.abs(firstTimeStamp.until(lastTimeStamp, unit));
        return difference / totalPixels;
    }

    public static getJaegerPixelsPerMonth(context: JaegerContext): number {
        return context.magnification;
    }

    public static getPixelsPerTime(
        user: FullUserModel,
        totalPixels: number = $(this.Selectors.GOALS_TRAY_CONTAINER).width(),
        unit: ChronoUnit = ChronoUnit.MONTHS
    ): number {
        const firstTimeStamp: LocalDate = this.getTimelineStart(user);
        const lastTimeStamp: LocalDate = this.getTimelineEnd(user);
        const difference: number = Math.abs(firstTimeStamp.until(lastTimeStamp, unit));
        return totalPixels / difference;
    }

    public static getPixelsBetweenDates(
        user: FullUserModel,
        firstDate: LocalDate,
        secondDate: LocalDate,
        totalPixels: number = $(this.Selectors.GOALS_TRAY_CONTAINER).width(),
        unit: ChronoUnit = ChronoUnit.DAYS
    ): number {
        const pixelsPerTime = this.getPixelsPerTime(user, totalPixels, unit);
        const difference: number = Math.abs(firstDate.until(secondDate, unit));
        return difference * pixelsPerTime;
    }

    public static getJaegerX(context: JaegerContext, user: FullUserModel, date: LocalDate): number {
        const pixelsPerMonth: number = this.getJaegerPixelsPerMonth(context);
        const monthsFromStart: number = this.getTimelineStart(user).until(date, ChronoUnit.MONTHS);
        return pixelsPerMonth * monthsFromStart + UnitService.JAEGER_MARGIN_LEFT;
    }

    public static getJaegarDateFromPixels(context: JaegerContext, user: FullUserModel, positionX: number): LocalDate {
        const pixelsPerMonth: number = this.getJaegerPixelsPerMonth(context);
        const monthsFromStart: number = Math.floor((positionX - UnitService.JAEGER_MARGIN_LEFT) / pixelsPerMonth);
        return this.getTimelineStart(user).plusMonths(monthsFromStart);
    }

    public static getJaegerPixelsBetweenDates(
        context: JaegerContext,
        user: FullUserModel,
        date1: LocalDate,
        date2: LocalDate
    ): number {
        const x1: number = this.getJaegerX(context, user, date1);
        const x2: number = this.getJaegerX(context, user, date2);
        return Math.abs(x2 - x1);
    }

    /** Get the left margin of the jaeger container */
    public static getJaegerContainerMarginLeft(): number {
        const jaegerContainer: Element = document.getElementsByClassName('jaegerContainer')[0];
        const marginLeft: string = window.getComputedStyle(jaegerContainer).marginLeft;
        return FormatService.formatStringToNumber(marginLeft) || 0;
    }

    /** Get the center of the jaeger container (timeline) */
    public static getJaegerContainerCenter(): number {
        const jaegerContainer: Element = document.getElementsByClassName('jaegerContainer')[0];
        const marginLeft = this.getJaegerContainerMarginLeft();
        return jaegerContainer.clientWidth / 2 + marginLeft || 0;
    }

    /**
     * cross-browser solution to get viewport width
     */
    public static getViewportWidth(): number {
        const userAgent = window.navigator.userAgent;
        if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
            return window.innerWidth - 109;
        } else {
            return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
        }
    }

    public static getViewportHeight(): number {
        const userAgent = window.navigator.userAgent;
        if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
            return window.innerHeight - 109;
        } else {
            return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        }
    }

    /**
     * returns width stats to render bars, based on zoom level
     * @param zoomLevel
     */
    public static getBarStatsInVh(zoomLevel: string): BarStats {
        const initialMargin: number = 0.585;

        switch (zoomLevel) {
            case 'months':
                return {
                    barWidth: 3.221,
                    initialMargin,
                    intraBarMargin: 2.855
                };
            case 'overall':
                return {
                    barWidth: 100 / 20.5,
                    initialMargin,
                    intraBarMargin: 1.391
                };
            case 'years':
                return {
                    barWidth: 2.343,
                    initialMargin,
                    intraBarMargin: 1.391
                };
            default:
                return {
                    barWidth: 2.343,
                    initialMargin,
                    intraBarMargin: 1.391
                };
        }
    }

    public static getBarStatsInPx(cashFlow: CashFlowModel[], zoomLevel: string): BarStats {
        const verticalScaleWidth: number = $(this.Selectors.VERTICAL_SCALE).width() + 16;
        const drawerWidth: number = this.getDrawerWidth();
        let timelineWidth: number = drawerWidth
            ? Math.floor(this.getViewportWidth() - drawerWidth - verticalScaleWidth)
            : Math.floor(this.getViewportWidth() * 0.76 - verticalScaleWidth);
        if (timelineWidth < 0) {
            timelineWidth = $(this.Selectors.WORLDLINE_CONTAINER).width() || 980;
        }
        const initialMargin: number = 8;

        switch (zoomLevel) {
            case 'months':
                return {
                    barWidth: Math.floor(timelineWidth / 12.5),
                    initialMargin,
                    intraBarMargin: 8
                };

            case 'overall': {
                const barWidth = timelineWidth / cashFlow.length;
                return {
                    barWidth: Math.floor(Math.max(barWidth, 4)),
                    initialMargin,
                    intraBarMargin: 4
                };
            }

            case 'years':
            default: {
                let barWidth = timelineWidth / cashFlow.length;
                if (barWidth < 30) {
                    barWidth = Math.floor(timelineWidth / 20.5);
                }
                return {
                    barWidth,
                    initialMargin,
                    intraBarMargin: 8
                };
            }
        }
    }

    public static getNowPosition(cashFlows: CashFlowModel[], barWidth: number, columns: number[]): number {
        const bars: CashFlowModel[] = columns.map((column: number) => {
            return cashFlows[column];
        });

        const now: LocalDate = LocalDate.now();
        let index: number = 0;
        // We need to use a classic for loop here, since it is not possible to break from a forEach loop.
        for (let i = 0; i < bars.length; i++) {
            const column: CashFlowModel = bars[i];
            // As the first bars will be before the current date, checking conditions in this order saves time.
            if (now.isBefore(column.endDate) && now.isAfter(column.startDate)) {
                index = i;
                break;
            }
        }

        const bar: CashFlowModel = bars[index];
        const startAdjusted: number = Date.parse(bar.startDate.toString()).valueOf();
        const endAdjusted: number = Date.parse(bar.endDate.toString()).valueOf() - startAdjusted;
        const nowAdjusted: number = Date.parse(now.toString()).valueOf() - startAdjusted;
        const additionalOffset: number = barWidth * (nowAdjusted / endAdjusted);

        return barWidth * index + additionalOffset;
    }

    public static getLeftBoundary(e: any): number {
        return e.target.getBoundingClientRect().left;
    }

    public static getTrayHeightInVh(numberOfRows: number): number {
        return Math.min(56, numberOfRows > 3 ? 26 + (numberOfRows - 3) * 9.375 : 26);
    }

    public static getTrayPaddingInVh(): number {
        return 100 * (15 / this.getViewportHeight());
    }

    public static getMainHeaderInVh(): number {
        return 100 * (74 / this.getViewportHeight());
    }

    public static getWorldlineHeightInVh(numberOfRows: number): number {
        const trayHeightInVh = this.getTrayHeightInVh(numberOfRows);

        return 100 - (trayHeightInVh + this.getTrayPaddingInVh() + this.getMainHeaderInVh());
    }

    public static getVerticalScaleHeightInVh(numberOfRows: number): number {
        const trayHeightInVh = this.getTrayHeightInVh(numberOfRows);

        return 100 - (trayHeightInVh + this.getTrayPaddingInVh() + this.getMainHeaderInVh());
    }

    public static getDateForPixels(user: any, xpos: number, columnWidth: number): LocalDate {
        const x: number = Math.max(0, xpos - UnitService.SCROLL_LEFT_MARGIN + this.getScrollWidth());
        const offset: number = Math.floor(x / columnWidth);
        const remainder: number = x % columnWidth;

        if (!user.cashFlow || !user.cashFlow[offset]) {
            return null;
        }
        const daysPerColumn: number = user.cashFlow[offset].startDate.until(
            user.cashFlow[offset].endDate,
            ChronoUnit.DAYS
        );
        const days: number = Math.floor((daysPerColumn * remainder) / columnWidth);

        return user.cashFlow[offset].startDate.plusDays(days);
    }

    public static getPositionForLocalDate(user: FullUserModel, date: LocalDate): number {
        const zoomLevel: string = this.getZoomLevel();
        const barStats: BarStats = UnitService.getBarStatsInPx(user.cashFlow, zoomLevel);
        const columnWidth: number = barStats.barWidth;

        const offset = CashFlowService.getCashFlowIndexWithDate(user.cashFlow, date);
        if (!offset) return 0;
        const cashflow: CashFlowModel = user.cashFlow[offset];
        const dateOffset: number =
            cashflow.startDate.until(date, ChronoUnit.DAYS) /
            cashflow.startDate.until(cashflow.endDate, ChronoUnit.DAYS);
        const monthOffset: number = zoomLevel === 'months' ? 45 : 0;
        return offset * columnWidth + dateOffset * columnWidth + monthOffset;
    }

    public static getLocalDateForDropPosition(user: FullUserModel, pageX: number): any {
        const zoomLevel: string = this.getZoomLevel();
        const barStats = UnitService.getBarStatsInPx(user.cashFlow, zoomLevel);
        const columnWidth = barStats.barWidth;
        const tray = $(this.Selectors.GOALS_TRAY_CONTAINER);
        if (!tray) return LocalDate.now();
        const trayLeft = $(this.Selectors.GOALS_TRAY_CONTAINER).offset().left;
        const x = Math.max(UnitService.SCROLL_LEFT_MARGIN + pageX - trayLeft - barStats.initialMargin, 0);
        const offset = x / columnWidth;
        const iOffset = Math.floor(offset);
        // if it's past the end of timeline, set it to the last entry in timeline
        const cashFlowElement = user.cashFlow[iOffset < user.cashFlow.length ? iOffset : user.cashFlow.length - 1];

        switch (zoomLevel) {
            case 'months':
                return cashFlowElement.startDate;

            case 'years':
            case 'overall':
            default:
                const months = Math.floor((offset % 1) * 12);
                return cashFlowElement.startDate.plusMonths(months);
        }
    }

    public static isColumnOnRightSide(div: any): boolean {
        const timelineCenter = (this.getViewportWidth() - this.getDrawerWidth()) / 2;
        const columnCenter = $(div).position().left - this.getScrollWidth() + $(div).width() / 2;
        return columnCenter > timelineCenter;
    }

    public static buildCardRows(rawCards: CardModel[], user: FullUserModel): CardModel[][] {
        // minwidth should be the width of the goals in the drop zone (50)
        const minWidth = Math.round(Number(this.getTimePerPixel(user)) * 50);

        // get fast access to linked cards since they are not directly referenced from each other
        const cardLinkMap: Map<number, CardModel> = new Map<number, CardModel>();
        rawCards.forEach((card) => cardLinkMap.set(card.id, card));

        let cardPlacements: CardPlacement[] = rawCards
            // sort it so we can always start with the first available and work toward right
            .sort(
                (c1: CardModel, c2: CardModel) =>
                    c1.startDate.compareTo(c2.startDate) || c1.endDate.compareTo(c2.endDate)
            )

            // remove later of the linked cards as they will be dropped on separately later
            .filter((card) => {
                const linkedCard: CardModel = cardLinkMap.get(_.get(card, 'attributes.linked_card_id'));
                return !linkedCard || linkedCard.startDate.isAfter(card.startDate);
            })

            // return the placements with endDate being the end of the linked card if there was one
            .map((card) => {
                const linkedCard: CardModel = cardLinkMap.get(_.get(card, 'attributes.linked_card_id'));
                const endCard: CardModel = linkedCard || card;
                return {
                    card,
                    linkedCard,
                    placeStartDate: card.startDate,
                    placeEndDate: endCard.startDate.plusDays(
                        Math.max(endCard.startDate.until(endCard.endDate, ChronoUnit.DAYS), minWidth)
                    )
                };
            });

        const rows: CardModel[][] = new Array<CardModel[]>();
        // as long as there are unplaced cards, keep adding rows
        while (cardPlacements.length) {
            // start with the first card, and fill this row with the first non-overlapping cards
            const row = [cardPlacements.shift()];
            let prevEnd = row[0].placeEndDate;
            cardPlacements.forEach((cp: CardPlacement) => {
                if (!cp.placeStartDate.isBefore(prevEnd)) {
                    // found the next non-overlapping card, add to this row
                    row.push(cp);
                    prevEnd = cp.placeEndDate;
                }
            });

            // add the cards and their linked cards into the next row
            rows.push([...row.map((cp) => cp.card), ...row.map((cp) => cp.linkedCard).filter((card) => !!card)]);

            // remove everything we just placed
            cardPlacements = cardPlacements.filter((cp) => !row.includes(cp));
        }
        return rows;
    }

    public static isNarrow(): boolean {
        return window.innerWidth <= 768;
    }
}
